import { useEffect, useMemo, useState } from "react";
import { usePageState } from "@/contexts/NavigationContext";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import TemplatePicker, { withTemplate } from "@/components/TemplatePicker";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { CalendarIcon, PrinterIcon, PaperPlaneTiltIcon, EnvelopeIcon, XIcon, CheckIcon, SpinnerIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { MultiPaymentModal } from "@/pages/_payments/ReceivablesTab";
import { useLiveSearch, SearchBox } from "@/components/LiveSearch";

/**
 * PAG-05 — Pestaña "Recibos del mes" del módulo Pagos y Cobranza.
 * Por default muestra el mes en curso. Permite filtro por rango de fechas y consulta por cliente.
 */
export default function ReceiptsTab() {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + "01";
  const [desde, setDesde] = useState(firstOfMonth);
  const [hasta, setHasta] = useState(today);
  const [q, setQ] = usePageState("q", "");
  const [data, setData] = useState({ items: [], count: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const esAdmin = Boolean(user?.effective?.is_admin || user?.role === "admin");
  const [emailFor, setEmailFor] = useState(null); // payment row to send by email
  const [editando, setEditando] = useState(null);  // {pago, group, bankAccounts} — Editar recibo (solo Administrador)

  const abrirEdicion = async (p) => {
    try {
      const [{ data }, { data: ba }] = await Promise.all([
        api.get(`/payments/${p.id}/edicion`), api.get("/treasury/accounts").catch(() => ({ data: [] }))]);
      const facturas = data.facturas || [];
      setEditando({
        pago: data.pago, bankAccounts: (ba || []).filter((a) => a.activo !== false),
        group: { cliente_id: data.cliente.id, cliente_nombre: data.cliente.nombre, cliente_codigo: data.cliente.codigo, facturas,
                 total: facturas.reduce((t, f) => t + Number(f.saldo_pendiente || 0), 0),
                 vencido: facturas.filter((f) => f.vencida).reduce((t, f) => t + Number(f.saldo_pendiente || 0), 0) },
      });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (desde) params.desde = new Date(desde).toISOString();
      if (hasta) params.hasta = new Date(hasta + "T23:59:59").toISOString();
      const { data: r } = await api.get("/receipts", { params });
      setData(r);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const busqueda = useLiveSearch(data.items, (p) => [p.cliente_nombre, p.cliente_codigo, p.factura_numero, p.referencia, p.numero_recibo],
    { estado: [q, setQ] });
  const filtered = busqueda.resultados;

  const openReceipt = async (pid) => {
    try {
      const res = await api.get(withTemplate(`/payments/${pid}/receipt-pdf`, "recibo"), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <>
      <div className="flex justify-end mb-2"><TemplatePicker tipo="recibo" /></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        <Kpi label="Recibos en el rango" v={data.count} />
        <Kpi label="Total cobrado" v={fmtL(data.total)} accent="green" />
        <Kpi label="Rango" v={`${desde || "—"} → ${hasta || "—"}`} accent="" small />
      </div>

      <div className="bg-card border border-border/60 rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-end" data-testid="receipts-filters">
        <div>
          <Lbl><CalendarIcon size={12} className="inline mr-1" /> Desde</Lbl>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="receipts-desde"
            className="bg-app border border-border rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <Lbl><CalendarIcon size={12} className="inline mr-1" /> Hasta</Lbl>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="receipts-hasta"
            className="bg-app border border-border rounded-md px-3 py-2 text-sm" />
        </div>
        <button onClick={load} data-testid="receipts-apply"
          className="bg-agro-green text-black font-medium rounded-md px-4 py-2 text-sm h-[38px]">
          Aplicar
        </button>
        <div className="flex-1 min-w-64">
          <Lbl>Buscar</Lbl>
          <SearchBox busqueda={busqueda} placeholder="Cliente, código, recibo, factura o referencia…" testid="receipts-search" label="Buscar recibos" />
        </div>
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left">
                <th className="p-3 pl-5">Recibo</th>
                <th className="p-3">Fecha</th>
                <th className="p-3">Cliente</th>
                <th className="p-3">Factura(s)</th>
                <th className="p-3">Método</th>
                <th className="p-3">Banco/Ref</th>
                <th className="p-3 text-right">Monto</th>
                <th className="p-3 text-center">Comprob.</th>
                <th className="p-3 pr-5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="9" className="p-12 text-center text-text-muted">Sin recibos en este rango</td></tr>
              ) : filtered.map((p) => {
                const allocs = p.allocations || [];
                const facturas = allocs.length > 0 ? allocs.map((a) => a.factura_numero).join(", ") : (p.factura_numero || "—");
                const monto = p.monto_total || p.monto || 0;
                return (
                  <tr key={p.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`receipt-row-${p.id.slice(0,8)}`}>
                    <td className="p-3 pl-5 text-xs font-mono font-semibold">{p.numero_recibo || "—"}</td>
                    <td className="p-3 text-xs">{fmtDate(p.fecha)}</td>
                    <td className="p-3 text-sm">{p.cliente_codigo && <span className="font-mono text-[11px] text-agro-green mr-1.5">{p.cliente_codigo}</span>}{p.cliente_nombre}</td>
                    <td className="p-3 text-xs font-mono text-agro-green">{facturas}</td>
                    <td className="p-3 text-xs capitalize">{p.metodo}</td>
                    <td className="p-3 text-xs text-text-secondary">{p.banco || "—"} {p.referencia && <span className="text-text-muted">· {p.referencia}</span>}</td>
                    <td className="p-3 text-right font-medium text-agro-green">{fmtL(monto)}</td>
                    <td className="p-3 text-center text-xs">
                      {(p.comprobantes || []).length > 0 ? (
                        <span className="inline-block px-2 py-0.5 bg-agro-green/15 text-agro-green rounded text-[10px]">
                          {p.comprobantes.length} adj.
                        </span>
                      ) : <span className="text-text-muted">—</span>}
                    </td>
                    <td className="p-3 pr-5 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <button onClick={() => openReceipt(p.id)} data-testid={`open-receipt-${p.id.slice(0,8)}`}
                          className="text-xs px-3 py-1.5 border border-agro-yellow/40 text-agro-yellow rounded hover:bg-agro-yellow/10 inline-flex items-center gap-1.5">
                          <PrinterIcon size={12} /> Ver / Imprimir
                        </button>
                        <button onClick={() => setEmailFor(p)} data-testid={`email-receipt-${p.id.slice(0,8)}`}
                          className="text-xs px-3 py-1.5 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded inline-flex items-center gap-1.5"
                          title="Enviar recibo por correo">
                          <PaperPlaneTiltIcon size={12} weight="bold" /> Enviar
                        </button>
                        {esAdmin && p.estado !== "anulado" && p.metodo !== "saldo_favor" && (
                          <button data-testid={`edit-payment-${p.id.slice(0,8)}`} title="Editar recibo (mismo número; recalcula saldos, banco y contabilidad)"
                            onClick={() => abrirEdicion(p)}
                            className="text-xs px-2 py-1.5 border border-agro-green/40 text-agro-green rounded hover:bg-agro-green/10">Editar recibo</button>
                        )}
                        {esAdmin && p.estado !== "anulado" && p.metodo !== "saldo_favor" && (
                          <button data-testid={`void-payment-${p.id.slice(0,8)}`} title="Anular pago (repone saldos y revierte tesorería y asiento)"
                            onClick={async () => {
                              const motivo = window.prompt(`Motivo para anular el recibo ${p.numero_recibo || ""}:`);
                              if (!motivo) return;
                              try { await api.post(`/payments/${p.id}/anular`, { motivo }); toast.success("Pago anulado"); load(); }
                              catch (e) { toast.error(apiErr(e)); }
                            }}
                            className="text-xs px-2 py-1.5 border border-red-500/40 text-red-300 rounded hover:bg-red-500/10">Anular</button>
                        )}
                        {p.estado === "anulado" && <span className="text-[10px] uppercase text-red-300">Anulado</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {emailFor && (
        <SendReceiptEmailModal payment={emailFor} onClose={() => setEmailFor(null)} />
      )}
      {editando && (
        <MultiPaymentModal group={editando.group} bankAccounts={editando.bankAccounts} edicion={editando.pago}
          onClose={() => setEditando(null)} onSaved={() => { setEditando(null); load(); }} />
      )}
    </>
  );
}

function SendReceiptEmailModal({ payment, onClose }) {
  const numero = payment.numero_recibo || (payment.id || "").slice(0, 8).toUpperCase();
  const [emails, setEmails] = useState("");
  const [subject, setSubject] = useState(`Recibo de Pago ${numero} · AGROPORT`);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Pre-cargar email del cliente
  useEffect(() => {
    (async () => {
      if (payment.cliente_id) {
        try {
          const { data } = await api.get(`/clientes/${payment.cliente_id}`);
          if (data?.email) setEmails(data.email);
        } catch { /* ignore */ }
      }
    })();
  }, [payment.cliente_id]);

  const submit = async (e) => {
    e.preventDefault();
    const list = emails.split(",").map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) { toast.error("Agrega al menos un correo destinatario"); return; }
    setSending(true);
    try {
      await api.post(`/payments/${payment.id}/send-email`, {
        to: list,
        subject,
        message: message || undefined,
      });
      setSent(true);
      toast.success(`Recibo enviado a ${list.length} destinatario(s)`);
      setTimeout(() => onClose(), 1800);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSending(false);
    }
  };

  const monto = payment.monto_total || payment.monto || 0;

  const em = useModalForm({ value: { emails, subject, message }, onClose: onClose, onSave: submit, mensaje: "¿Está seguro de que desea enviar el correo?" });
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" {...em.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="send-receipt-email-modal">
        <form onSubmit={em.guardar}>
          <div className="p-5 border-b border-border flex items-start justify-between">
            <div>
              <h3 className="font-heading text-lg flex items-center gap-2">
                <EnvelopeIcon size={20} className="text-agro-green" /> Enviar recibo por email
              </h3>
              <p className="text-xs text-text-muted mt-1">
                Recibo <span className="font-mono text-agro-yellow">#{numero}</span> · {payment.cliente_nombre} · <span className="text-agro-green font-semibold">{fmtL(monto)}</span>
              </p>
              <p className="text-[10px] text-text-muted mt-0.5">Se enviará el PDF desde notificaciones@agroporthonduras.com</p>
            </div>
            <button aria-label="Cerrar" type="button" onClick={em.cerrar} className="text-text-muted hover:text-text-primary p-1" data-testid="receipt-email-close">
              <XIcon size={18} />
            </button>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted font-medium mb-1.5 block">Destinatarios <span className="text-red-400">*</span></label>
              <input
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder="cliente@email.com, otro@email.com"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none"
                data-testid="receipt-email-to"
                autoFocus
              />
              <div className="text-[10px] text-text-muted mt-1">Separa varios correos con coma.</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted font-medium mb-1.5 block">Asunto</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none"
                data-testid="receipt-email-subject"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted font-medium mb-1.5 block">
                Mensaje adicional <span className="text-text-muted/70 normal-case">(opcional)</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="Ej: Adjunto el recibo oficial correspondiente a su pago. Gracias por su preferencia."
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none resize-none"
                data-testid="receipt-email-message"
              />
            </div>
          </div>
          <div className="p-4 border-t border-border flex justify-end gap-2 bg-app/40">
            <button type="button" onClick={em.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary hover:bg-white/5" data-testid="receipt-email-cancel">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={(sending || sent) || em.saving}
              className="px-4 py-2 text-sm bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md disabled:opacity-50 flex items-center gap-1.5"
              data-testid="receipt-email-send"
            >
              {sending ? (<><SpinnerIcon size={14} className="animate-spin" /> Enviando…</>) :
                sent ? (<><CheckIcon size={14} weight="bold" /> Enviado</>) :
                (<><PaperPlaneTiltIcon size={14} weight="bold" /> Enviar recibo</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Kpi({ label, v, accent, small }) {
  const map = { green: "text-agro-green", yellow: "text-agro-yellow", red: "text-red-400", "": "text-text-primary" };
  return (
    <div className="bg-card border border-border/60 rounded-lg p-5">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`${small ? "text-base font-mono mt-1" : "font-heading text-2xl font-semibold mt-1"} ${map[accent || ""]}`}>{v}</div>
    </div>
  );
}
const Lbl = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1 block">{children}</label>;
